import {APP_INITIALIZER, LOCALE_ID, NgModule} from "@angular/core";
import {BrowserModule} from "@angular/platform-browser";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";

import {
    I18NextLoadResult,
    I18NEXT_SERVICE,
    ITranslationService,
} from "angular-i18next";
import HttpApi from "i18next-http-backend";

import {AppComponent} from "./app.component";
import {AppRoutingModule} from "./app-routing.module";
import {AngularSvgIconModule} from "angular-svg-icon";
import {AuthenticationModule} from "./authentication/authentication.module";
import {CognitoAuthenticationManager} from "./shared/services/cognito-authentication-manager";
import {registerLocaleData} from "@angular/common";

import {AuthenticationManager} from "./shared/services/authentication-manager";
import {i18nextOptions} from "./internalization/i18next-options";
import {LanguageDetectorService} from "./internalization/language-detector.service";
import {SharedModule} from "./shared/shared.module";
import {DxDrawerModule} from "devextreme-angular";
import {ApplicationLayoutModule} from "./application-layout/application-layout.module";

import localeIt from "@angular/common/locales/it";
import localeEn from "@angular/common/locales/en";
import localeCs from "@angular/common/locales/cs";
import localeDe from "@angular/common/locales/de";
import localeEs from "@angular/common/locales/es";
import localeFi from "@angular/common/locales/fi";
import localeFr from "@angular/common/locales/fr";
import localeNl from "@angular/common/locales/nl";
import localePl from "@angular/common/locales/pl";
import {DialogsModule} from "./dialogs/dialogs.module";
import {provideLottieOptions} from "ngx-lottie";
import {StoreModule} from "@ngrx/store";
import {installationReducer} from "./state/installation/installation.reducers";
import {userReducer} from "./state/user/user.reducers";
import {MqttModule} from "ngx-mqtt";
import {initializeApp, provideFirebaseApp} from "@angular/fire/app";
import {getMessaging, provideMessaging} from "@angular/fire/messaging";
import {environment} from "../environments/environment";
import {ServiceWorkerModule} from "@angular/service-worker";
import {NotificationsManagerService} from "./notifications/services/notifications-manager.service";
import { currentCallReducer } from "./state/call/current-call.reducers";

registerLocaleData(localeIt);
registerLocaleData(localeEn);
registerLocaleData(localeCs);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeFi);
registerLocaleData(localeFr);
registerLocaleData(localeNl);
registerLocaleData(localePl);

export function appInit(i18next: ITranslationService) {
    return () => {
        const promise: Promise<I18NextLoadResult> = i18next
            .use(HttpApi)
            .use(LanguageDetectorService)
            .init(i18nextOptions);
        return promise;
    };
}

export function localeIdFactory(i18next: ITranslationService) {
    return i18next.language;
}

export const I18N_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        useFactory: appInit,
        deps: [I18NEXT_SERVICE],
        multi: true,
    },
    {
        provide: LOCALE_ID,
        deps: [I18NEXT_SERVICE],
        useFactory: localeIdFactory,
    },
];

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        DxDrawerModule,
        SharedModule,
        ApplicationLayoutModule,
        AngularSvgIconModule.forRoot(),
        DialogsModule,
        AppRoutingModule,
        AuthenticationModule,
        ServiceWorkerModule,
        MqttModule.forRoot({
            connectOnCreate: false
        }),
        StoreModule.forRoot({
            installation: installationReducer,
            user: userReducer,
            currentCall: currentCallReducer
        }),
    ],
    providers: [
        I18N_PROVIDERS,
        {provide: AuthenticationManager, useClass: CognitoAuthenticationManager},
        NotificationsManagerService,
        provideLottieOptions({
            player: () => import("lottie-web"),
        }),
        provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
        provideMessaging(() => getMessaging())
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
}
